    import React, { useState, useEffect, useRef, useCallback } from "react";
    import { Users, TrendingUp, FileText, Activity, Calendar, Eye, EyeOff } from "lucide-react";
    import {CartesianGrid,XAxis,YAxis,Tooltip,ResponsiveContainer,PieChart,Pie,Cell,AreaChart,Area,Legend,BarChart,Bar,} from "recharts";
    import html2canvas from "html2canvas";
    import jsPDF from "jspdf";
    import { useNavigate } from "react-router-dom";
    import "../../Styling/DashReport.css";
    import { useAuth } from "../../Context/AuthContext";
    import { Base_URL } from "../../url";

    const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const CHART_COLORS = ["#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#ef4444"];

    const SalesDashboard = () => {
      const { user } = useAuth();
      const navigate = useNavigate();
      const dashboardRef = useRef();
      const debounceTimeoutRef = useRef(null);

      // States
      const [chartVisibility, setChartVisibility] = useState({
        revenue: true,
        customerSegments: true,
        weeklySales: true 
      });

      const [chartDataVisibility, setChartDataVisibility] = useState({
        revenue: true,
        customerSegments: true,
        weeklySales: true
      });

      const [formData, setFormData] = useState({
        orgId: String(user?.organisationId || ""),
        companyCode: "",
        fy: "",
        postingDate: new Date().toISOString().split("T")[0],
      });

      const [orgIds, setOrgIds] = useState([]);
      const [companyCodes, setCompanyCodes] = useState([]);
      const [financialYears, setFinancialYears] = useState([]);
      const [results, setResults] = useState([]);
      const [supplyTypes, setSupplyTypes] = useState([]);
      const [filteredMonthlyRevenue, setFilteredMonthlyRevenue] = useState([]);
      const [salespersonData, setSalespersonData] = useState([]);
      const [weeklySalesData, setWeeklySalesData] = useState([]);
      const [loading, setLoading] = useState(true);
      const [chartLoading, setChartLoading] = useState(true);
      const [pdfLoading, setPdfLoading] = useState(false);
      const [error, setError] = useState("");

      // Utility Functions
      const formatAmount = useCallback((amount) => {
        if (amount === null || amount === undefined || amount === 0) return "0.00";
        const num = parseFloat(amount);
        return isNaN(num) ? "0.00" : num.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }, []);

      // Chart visibility toggle
      const toggleChartVisibility = (chartName) => {
        setChartVisibility(prev => ({
          ...prev,
          [chartName]: !prev[chartName]
        }));
      };

      // Chart data visibility toggle
      const toggleChartDataVisibility = (chartName) => {
        setChartDataVisibility(prev => ({
          ...prev,
          [chartName]: !prev[chartName]
        }));
      };

      // Check if any chart is visible
      const anyChartVisible = Object.values(chartVisibility).some(v => v);

      // Navigation to details page
      const handleChartDetails = (chartType) => {
        let data = [];
        let title = '';

        switch (chartType) {
          case 'revenue':
            data = filteredMonthlyRevenue;
            title = 'Monthly Revenue Data - Detailed View';
            break;
          case 'customerSegments':
            // Remove color property for details page
            data = supplyTypes.map(({ color, ...rest }) => rest);
            title = 'Customer Segments - Detailed View';
            break;
          case 'weeklySales':
            data = weeklySalesData;
            title = 'Weekly Sales Performance - Detailed View';
            break;
          default:
            return;
        }

        // Navigate to details page with data
        navigate('/chart-details', {
          state: {
            chartData: data,
            chartTitle: title,
            chartType: chartType,
            filters: formData
          }
        });
      };

      // API Functions
      const fetchOrgIds = useCallback(async () => {
        try {
          if (!user?.userId) return;
          const res = await fetch(`${Base_URL}/projectDashboard/orgids/${user.userId}`);
          const data = await res.json();
          setOrgIds(Array.isArray(data) ? data : []);
          if (Array.isArray(data) && data.length === 1) {
            const orgId = String(data[0]);
            setFormData((prev) => ({ ...prev, orgId }));
            fetchCompanyCodes(orgId);
          } 
        } catch (err) {
          console.error(err);
          setError("Failed to load Organization IDs");
        }
      }, [user]);

      const fetchFinancialYears = useCallback(async () => {
        try {
          const res = await fetch(`${Base_URL}/projectDashboard/financialyears`);
          const data = await res.json();
          setFinancialYears(Array.isArray(data) ? data : []);
        } catch (err) {
          console.error(err);
          setError("Failed to load Financial Years");
        }
      }, []);

      const fetchCompanyCodes = useCallback(async (orgId) => {
        try {
          if (!orgId) return;
          const res = await fetch(`${Base_URL}/projectDashboard/companycodes/${orgId}`);
          const data = await res.json();
          setCompanyCodes(Array.isArray(data) ? data : []);
        } catch (err) {
          console.error(err);
          setError("Failed to load Company Codes");
        }
      }, []);

      const fetchResults = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
          const payload = {
            Org_Id: formData.orgId !== "ALL" ? formData.orgId : null,
            Doccc: formData.companyCode || null,
            Docfy: formData.fy || null,
            POSTING_DATE: formData.postingDate || null,
          };
          const response = await fetch(`${Base_URL}/ProjectDashboard/submit`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const data = await response.json();
          setResults(Array.isArray(data) ? data : []);
        } catch (err) {
          console.error(err);
          setError("Failed to load sales data");
        } finally {
          setLoading(false);
        }
      }, [formData]);

      const fetchMonthlyRevenue = useCallback(async () => {
        setChartLoading(true);
        try {
          const params = new URLSearchParams();
          if (formData.companyCode) params.append("doccc", formData.companyCode);
          if (formData.orgId && formData.orgId !== "ALL") params.append("orgId", formData.orgId);
          if (formData.postingDate) params.append("postingDate", formData.postingDate);
          if (formData.fy) params.append("docfy", formData.fy);

          const url = `${Base_URL}/ProjectDashboard/Monthly${params.toString() ? "?" + params.toString() : ""}`;
          const response = await fetch(url);
          const data = await response.json();

          const monthlyAggregated = {};
          (Array.isArray(data) ? data : []).forEach((item) => {
            const monthNumber = Number(item.month);
            const year = Number(item.year);
            const revenue = Number(item.revenue || 0);
            
            if (!isNaN(monthNumber) && monthNumber >= 1 && monthNumber <= 12) {
              const key = `${year}-${monthNumber}`;
              if (!monthlyAggregated[key]) {
                monthlyAggregated[key] = { monthNumber, year, revenue: 0 };
              }
              monthlyAggregated[key].revenue += revenue;
            }
          });

          const formattedData = Object.values(monthlyAggregated).map((item) => ({
            month: MONTH_NAMES[item.monthNumber - 1] || `Month ${item.monthNumber}`,
            revenue: item.revenue,
            year: item.year,
            monthNumber: item.monthNumber,
          }));

          formattedData.sort((a, b) => a.year !== b.year ? a.year - b.year : a.monthNumber - b.monthNumber);
          setFilteredMonthlyRevenue(formattedData);
        } catch (err) {
          console.error("Error fetching monthly revenue:", err);
          setFilteredMonthlyRevenue([]);
        } finally {
          setChartLoading(false);
        }
      }, [formData]);

    const fetchSupplyTypes = useCallback(async () => {
      try {
        const params = new URLSearchParams();
        if (formData.companyCode) params.append("doccc", formData.companyCode);
        
        // ✅ FIXED: Use correct parameter names and handle "ALL" case
        if (formData.orgId && formData.orgId !== "ALL") {
          params.append("orgId", formData.orgId); // Try this first
          params.append("docID", formData.orgId); // Fallback parameter
        }
        
        if (formData.postingDate) params.append("postingDate", formData.postingDate);
        if (formData.fy) params.append("docfy", formData.fy);

        console.log("Fetching supply types with params:", params.toString()); // Debug log

        const url = `${Base_URL}/ProjectDashboard/GetSupplyTypesTotal${params.toString() ? "?" + params.toString() : ""}`;
        const res = await fetch(url);
        
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        
        const data = await res.json();

        const arr = Array.isArray(data) ? data : [];
        const totalAmount = arr.reduce((sum, item) => sum + Number(item.amount || 0), 0);

        const formatted = arr.map((item, idx) => ({
          name: item.supplyTypeDescription || "Unknown",
          value: Number(item.count || 0),
          amount: Number(item.amount || 0),
          percentage: totalAmount > 0 ? ((Number(item.amount || 0) / totalAmount) * 100).toFixed(1) : "0.0",
          color: CHART_COLORS[idx % CHART_COLORS.length],
        }));

        console.log("Supply types data for org", formData.orgId, ":", formatted.length, "items"); // Debug log
        setSupplyTypes(formatted);
      } catch (err) {
        console.error("Error fetching supply types for org", formData.orgId, ":", err);
        setSupplyTypes([]);
      }
    }, [formData]);

      const fetchSalespersonData = useCallback(async () => {
        try {
          const payload = {
            Org_Id: formData.orgId !== "ALL" ? formData.orgId : null,
            Doccc: formData.companyCode || null,
            Docfy: formData.fy || null,
            POSTING_DATE: formData.postingDate ? formData.postingDate : null,
          };

          Object.keys(payload).forEach((key) => {
            if (payload[key] === null || payload[key] === "") delete payload[key];
          });

          const url = `${Base_URL}/ProjectDashboard/GetSalesDashboard`;
          const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });

          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          const data = await res.json();
          
          let processedData = [];
          if (Array.isArray(data) && data.length > 0) {
            const salespersonMap = {};

            data.forEach((item) => {
              const salespersonName = item.salesperson || item.salesPerson || "Unknown";
              const customerName = item.customer || item.clientName || "Unknown";

              if (!salespersonMap[salespersonName]) {
                salespersonMap[salespersonName] = {
                  salesperson: salespersonName,
                  customers: [],
                  subtotals: {
                    fY25: 0,
                    fY24: 0,
                    fY23: 0,
                    fY22: 0,
                    fY21: 0,
                    fY20: 0,
                    fY19: 0,
                  },
                };
              }

              const customer = {
                customer: customerName,
                fY25: Number(item.fY25 ?? item.fy25 ?? item.FY25 ?? item.amount ?? 0),
                fY24: Number(item.fY24 ?? item.fy24 ?? item.FY24 ?? 0),
                fY23: Number(item.fY23 ?? item.fy23 ?? item.FY23 ?? 0),
                fY22: Number(item.fY22 ?? item.fy22 ?? item.FY22 ?? 0),
                fY21: Number(item.fY21 ?? item.fy21 ?? item.FY21 ?? 0),
                fY20: Number(item.fY20 ?? item.fy20 ?? item.FY20 ?? 0),
                fY19: Number(item.fY19 ?? item.fy19 ?? item.FY19 ?? 0),
              };

              salespersonMap[salespersonName].customers.push(customer);

              salespersonMap[salespersonName].subtotals.fY25 += customer.fY25;
              salespersonMap[salespersonName].subtotals.fY24 += customer.fY24;
              salespersonMap[salespersonName].subtotals.fY23 += customer.fY23;
              salespersonMap[salespersonName].subtotals.fY22 += customer.fY22;
              salespersonMap[salespersonName].subtotals.fY21 += customer.fY21;
              salespersonMap[salespersonName].subtotals.fY20 += customer.fY20;
              salespersonMap[salespersonName].subtotals.fY19 += customer.fY19;
            });

            processedData = Object.values(salespersonMap);
          }

          setSalespersonData(processedData);
        } catch (err) {
          console.error("Failed to load salesperson data:", err);
          setSalespersonData([]);
        }
      }, [formData]);

    const fetchWeeklySalesData = useCallback(async () => {
      try {
        const params = new URLSearchParams();
        if (formData.companyCode) params.append("doccc", formData.companyCode);
        
        // ✅ FIXED: Handle "ALL" case and use correct parameters
        if (formData.orgId && formData.orgId !== "ALL") {
          params.append("orgId", formData.orgId);
        }
        
        if (formData.postingDate) params.append("postingDate", formData.postingDate);
        if (formData.fy) params.append("docfy", formData.fy);

        console.log("Fetching weekly sales with params:", params.toString()); // Debug log

        const url = `${Base_URL}/ProjectDashboard/GetWeeklySalesWithMonth${params.toString() ? "?" + params.toString() : ""}`;
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const formattedData = (Array.isArray(data) ? data : [])
          .map((item) => ({
            week: `Week ${Number(item.weekNumber ?? item.week ?? item.weekNo) || 0}`,
            month: item.monthName || item.month || "",
            amount: Number(item.totalAmount || item.amount || 0),
            weekNumber: Number(item.weekNumber ?? item.week ?? item.weekNo) || 0,
          }))
          .sort((a, b) => a.weekNumber - b.weekNumber);

        console.log("Weekly sales data for org", formData.orgId, ":", formattedData.length, "items"); // Debug log
        setWeeklySalesData(formattedData);
      } catch (err) {
        console.error("Error fetching weekly sales for org", formData.orgId, ":", err);
        setWeeklySalesData([]);
      }
    }, [formData]);

      const debouncedFetchData = useCallback(() => {
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = setTimeout(() => {
          const fetchPromises = [
            fetchResults(),
            fetchSupplyTypes(),
            fetchMonthlyRevenue(),
            fetchSalespersonData(),
            fetchWeeklySalesData(),
          ];
          Promise.all(fetchPromises).catch(err => {
            console.error("Error in debounced fetch:", err);
            setError("Failed to load some dashboard data");
          });
        }, 300);
      }, [formData, fetchResults, fetchSupplyTypes, fetchMonthlyRevenue, fetchSalespersonData, fetchWeeklySalesData]);

      // Effects
      useEffect(() => {
        const initializeDashboard = async () => {
          try {
            setLoading(true);
            setChartLoading(true);
            await Promise.all([
              fetchOrgIds(),
              fetchFinancialYears(),
              fetchCompanyCodes(user?.organisationId),
            ]);
            debouncedFetchData();
          } catch (err) {
            console.error(err);
            setError("Failed to load dashboard data");
          } finally {
            setLoading(false);
            setChartLoading(false);
          }
        };
        
        if (user?.organisationId) initializeDashboard();
        
        return () => {
          if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        };
      }, [user?.organisationId]);

      useEffect(() => {
        if (formData.orgId) debouncedFetchData();
      }, [formData.companyCode, formData.fy, formData.postingDate, formData.orgId, debouncedFetchData]);

      // Handlers
      const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (error) setError("");
      };

      // Optimized PDF Generation
      const handleDownloadPDF = async () => {
        setPdfLoading(true);
        try {
          // Create a clone of the dashboard element for PDF generation
          const element = dashboardRef.current;
          const clone = element.cloneNode(true);
          
          // Apply PDF-specific styles
          clone.style.width = '794px'; // A4 width in pixels
          clone.style.padding = '20px';
          clone.style.backgroundColor = '#ffffff';
          
          // Hide elements that shouldn't be in PDF
          const elementsToHide = clone.querySelectorAll('.pdf-loading, .spinner, .chart-toggle-btn, .hide-btn');
          elementsToHide.forEach(el => el.style.display = 'none');
          
          // Create a temporary container
          const tempContainer = document.createElement('div');
          tempContainer.style.position = 'fixed';
          tempContainer.style.left = '-9999px';
          tempContainer.style.top = '0';
          tempContainer.appendChild(clone);
          document.body.appendChild(tempContainer);

          const canvas = await html2canvas(clone, { 
            scale: 2, // Higher scale for better quality
            useCORS: true, 
            logging: false,
            backgroundColor: '#ffffff',
            removeContainer: true,
            width: 794, // A4 width
            height: clone.scrollHeight,
            windowWidth: 794,
            windowHeight: clone.scrollHeight
          });

          // Clean up temporary elements
          document.body.removeChild(tempContainer);

          const imgData = canvas.toDataURL("image/jpeg", 0.8); // Better quality
          
          // Create PDF
          const pdf = new jsPDF("p", "mm", "a4");
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          
          // Calculate dimensions to fit A4
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
          const imgWidthPdf = imgWidth * ratio;
          const imgHeightPdf = imgHeight * ratio;
          
          // Add image to PDF
          pdf.addImage(imgData, "JPEG", 0, 0, imgWidthPdf, imgHeightPdf);
          
          // Add metadata
          pdf.setProperties({
            title: `Sales Dashboard - ${formData.orgId}`,
            subject: 'Sales Dashboard Report',
            author: 'Sales Dashboard System',
            keywords: 'sales, dashboard, revenue, report',
            creator: 'Sales Dashboard'
          });

          // Clean up canvas
          canvas.width = 0;
          canvas.height = 0;
          
          // Save PDF
          pdf.save(`SalesDashboard_${formData.orgId}_${formData.fy || 'All'}_${new Date().toISOString().slice(0, 10)}.pdf`);
          
        } catch (err) {
          console.error("PDF Generation Error:", err);
          setError("Failed to generate PDF. Please try again.");
        } finally {
          setPdfLoading(false);
        }
      };

      // Custom Tooltips (keep your existing tooltip components)
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="custom-tooltip">
                    <p className="tooltip-label">
                        {data.month} {data.year}
                    </p>
                    <p className="tooltip-value">
                        Revenue: {data.hasData ? `₹${formatAmount(data.revenue)}` : 'No data'}
                    </p>
                    {!data.hasData && <p className="tooltip-note">(No revenue recorded)</p>}
                </div>
            );
        }
        return null;
    };

      const CustomPieTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
          const data = payload[0].payload;
          return (
            <div className="custom-tooltip">
              <p className="tooltip-label">{data.name}</p>
              <p className="tooltip-value">Count: {data.value}</p>
              <p className="tooltip-value">Amount: ₹{formatAmount(data.amount)}</p>
              <p className="tooltip-value">({data.percentage}%)</p>
            </div>
          );
        }
        return null;
      };

      const CustomBarTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
          const data = payload[0].payload;
          return (
            <div className="custom-tooltip">
              <p className="tooltip-label">{label}</p>
              <p className="tooltip-value">Week {data.weekNumber} - {data.month}</p>
              <p className="tooltip-value">Sales: ₹{formatAmount(data.amount)}</p>
            </div>
          );
        }
        return null;
      };

      // Chart Components (keep your existing chart components)
    const renderRevenueChart = () => {
        if (!chartVisibility.revenue) return null;
        
        // Create complete dataset with all months, filling gaps with 0 or null
        const createCompleteDataset = (rawData) => {
            if (!rawData || rawData.length === 0) return [];

            // Get all unique years from data
            const years = [...new Set(rawData.map(item => item.year))];
            const minYear = Math.min(...years);
            const maxYear = Math.max(...years);

            const completeData = [];
            
            // Create complete timeline
            for (let year = minYear; year <= maxYear; year++) {
                for (let month = 1; month <= 12; month++) {
                    const existingData = rawData.find(item => 
                        item.year === year && item.monthNumber === month
                    );
                    
                    completeData.push({
                        month: MONTH_NAMES[month - 1],
                        monthNumber: month,
                        year: year,
                        revenue: existingData ? existingData.revenue : 0, // Use 0 instead of null
                        hasData: !!existingData,
                        displayMonth: `${MONTH_NAMES[month - 1]} '${year.toString().slice(-2)}`
                    });
                }
            }

            return completeData;
        };

        const completeData = createCompleteDataset(filteredMonthlyRevenue);
        const totalRevenue = filteredMonthlyRevenue.reduce((sum, item) => sum + (item.revenue || 0), 0);

        return (
            <div className="chart-card">
                <div className="chart-header">
                    <div className="chart-title">
                        <TrendingUp />
                        <h3>Revenue Performance</h3>
                    </div>
                    <div className="chart-actions">
                        <button className="details-btn" onClick={() => handleChartDetails('revenue')} title="View detailed data">
                            <Eye size={16} /> Details
                        </button>
                        <button className="hide-btn" onClick={() => toggleChartDataVisibility('revenue')} title="Toggle chart data">
                            {chartDataVisibility.revenue ? <EyeOff size={16} /> : <Eye size={16} />}
                            {chartDataVisibility.revenue ? "Hide Data" : "Show Data"}
                        </button>
                    </div>
                </div>
                <div className="chart-subtitle">Monthly revenue trend {formData.fy ? `for FY ${formData.fy}` : 'for all years'}</div>
                <div className="chart-body">
                    {chartLoading ? (
                        <div className="loading-message">Loading revenue data...</div>
                    ) : chartDataVisibility.revenue ? (
                        <>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart 
                                    data={completeData} 
                                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                                >
                                    <defs>
                                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                                    <XAxis 
                                        dataKey="displayMonth"
                                        interval={0}
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <YAxis 
                                        domain={[0, 'dataMax + 10000']}
                                        tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                                    />
                                    <Tooltip content={<CustomTooltip/>}/>
                                    <Area
                                        type="monotone"
                                        dataKey="revenue"
                                        stroke="#2563eb"
                                        strokeWidth={3}
                                        fill="url(#colorRevenue)"
                                        fillOpacity={0.6}
                                        connectNulls={true}
                                        dot={(props) => {
                                            const { payload } = props;
                                            return payload.hasData ? (
                                                <circle 
                                                    cx={props.cx}
                                                    cy={props.cy}
                                                    r={4}
                                                    fill="#2563eb"
                                                    stroke="#ffffff"
                                                    strokeWidth={2}
                                                />
                                            ) : null;
                                        }}
                                        activeDot={{ 
                                            r: 6, 
                                            fill: '#1e40af',
                                            stroke: '#ffffff',
                                            strokeWidth: 2
                                        }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                            <div className="chart-footer">
                                <span>Total Revenue {formData.fy ? `(FY ${formData.fy})` : '(All Years)'}:</span>
                                <strong>₹{formatAmount(totalRevenue)}</strong>
                            </div>
                        </>
                    ) : (
                        <div className="no-chart-data">
                            <TrendingUp size={48} color="#6b7280"/>
                            <p>Chart data is hidden</p>
                            <p className="data-hint">Click "Show Data" to display the chart</p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

      const renderCustomerSegments = () => {
        if (!chartVisibility.customerSegments) return null;
        const totalAmount = supplyTypes.reduce((sum, item) => sum + (item.amount || 0), 0);

        return (
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-title">
                <Users />
                <h3>Customer Segments</h3>
              </div>
              <div className="chart-actions">
                <button className="details-btn" onClick={() => handleChartDetails('customerSegments')} title="View detailed data">
                  <Eye size={16} /> Details
                </button>
                <button className="hide-btn" onClick={() => toggleChartDataVisibility('customerSegments')} title="Toggle chart data">
                  {chartDataVisibility.customerSegments ? <EyeOff size={16} /> : <Eye size={16} />}
                  {chartDataVisibility.customerSegments ? "Hide Data" : "Show Data"}
                </button>
              </div>
            </div>
            <div className="chart-subtitle">Distribution by supply types</div>
            <div className="chart-body">
              {supplyTypes.length > 0 ? (
                chartDataVisibility.customerSegments ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={supplyTypes}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          fill="#8884d8"
                          paddingAngle={4}
                          dataKey="amount"
                          label={({ name, percentage }) => (percentage > 0 ? `${name}: ${percentage}%` : "")}
                        >
                          {supplyTypes.map((entry, idx) => (
                            <Cell key={idx} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomPieTooltip/>} />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          formatter={(value, entry) => (
                            <span style={{ color: '#333', fontSize: '12px' }}>
                              {value} (₹{formatAmount(entry.payload.amount)})
                            </span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="chart-footer">
                      <span>Total Amount:</span>
                      <strong>₹{formatAmount(totalAmount)}</strong>
                    </div>
                  </>
                ) : (
                  <div className="no-chart-data">
                    <Users size={48} color="#6b7280"/>
                    <p>Chart data is hidden</p>
                    <p className="data-hint">Click "Show Data" to display the chart</p>
                  </div>
                )
              ) : (
                <div className="no-chart-data">
                  <Users size={48} color="#6b7280"/>
                  <p>No customer segment data available</p>
                </div>
              )}
            </div>
          </div>
        );
      };

      const renderWeeklySalesChart = () => {
        if (!chartVisibility.weeklySales) return null;
        const totalWeeklySales = weeklySalesData.reduce((sum, item) => sum + (item.amount || 0), 0);

        return (
          <div className="chart-card">
            <div className="chart-header">
              <div className="chart-title">
                <Calendar />
                <h3>Weekly Sales Performance</h3>
              </div>
              <div className="chart-actions">
                <button className="details-btn" onClick={() => handleChartDetails('weeklySales')} title="View detailed data">
                  <Eye size={16} /> Details
                </button>
                <button className="hide-btn" onClick={() => toggleChartDataVisibility('weeklySales')} title="Toggle chart data">
                  {chartDataVisibility.weeklySales ? <EyeOff size={16} /> : <Eye size={16} />}
                  {chartDataVisibility.weeklySales ? "Hide Data" : "Show Data"}
                </button>
              </div>
            </div>
            <div className="chart-subtitle">Sales distribution across weeks</div>
            <div className="chart-body">
              {weeklySalesData.length > 0 ? (
                chartDataVisibility.weeklySales ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={weeklySalesData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                          dataKey="week"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={0}
                          fontSize={12}
                        />
                        <YAxis />
                        <Tooltip content={<CustomBarTooltip />} />
                        <Bar
                          dataKey="amount"
                          fill="#8B5CF6"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="chart-footer">
                      <span>Total Weekly Sales:</span>
                      <strong>₹{formatAmount(totalWeeklySales)}</strong>
                    </div>
                  </>
                ) : (
                  <div className="no-chart-data">
                    <Calendar size={48} color="#6b7280"/>
                    <p>Chart data is hidden</p>
                    <p className="data-hint">Click "Show Data" to display the chart</p>
                  </div>
                )
              ) : (
                <div className="no-chart-data">
                  <Calendar size={48} color="#6b7280"/>
                  <p>No weekly sales data available</p>
                </div>
              )}
            </div>
          </div>
        );
      };

      const renderSalesTable = () => {
        return (
          <div className="table-section">
            <div className="table-header"><Activity /><h3>Sales Overview</h3></div>
            <div className="table-scroll-container">
              {loading ? <div className="loading-message">Loading sales data...</div> :
                results.length > 0 ? (
                  <table className="styled-table">
                    <thead>
                      <tr><th>Company Code</th><th>FY</th><th>Client Name</th><th>Project Name</th><th className="saleshead">Sales</th></tr>
                    </thead>
                    <tbody>
                      {results.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.companyCode || "-"}</td>
                          <td>{item.fy || "-"}</td>
                          <td>{item.clientName || "-"}</td>
                          <td>{item.projectName || "-"}</td>
                          <td className="amount">₹{formatAmount(item.totalAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4}>Total</td>
                        <td className="amount">₹{formatAmount(results.reduce((sum, r) => sum + (r.totalAmount || 0), 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <div className="no-data">
                    <Activity size={48} color="#6b7280"/>
                    <p>No sales data available for the selected filters</p>
                  </div>
                )
              }
            </div>
          </div>
        );
      };

      const renderSalespersonView = () => {
        if (!salespersonData || !Array.isArray(salespersonData) || salespersonData.length === 0) {
          return (
            <div className="table-section salesperson-table-container">
              <div className="table-header"><Users /><h3>Salesperson View</h3></div>
              <div className="no-data">
                <Users size={48} color="#6b7280" />
                <p>No salesperson data available</p>
              </div>
            </div>
          );
        }

        // Calculate Grand Total from all subtotals
        const grandTotal = salespersonData.reduce((total, sp) => ({
          fY25: total.fY25 + (sp.subtotals?.fY25 || 0),
          fY24: total.fY24 + (sp.subtotals?.fY24 || 0),
          fY23: total.fY23 + (sp.subtotals?.fY23 || 0),
          fY22: total.fY22 + (sp.subtotals?.fY22 || 0),
          fY21: total.fY21 + (sp.subtotals?.fY21 || 0),
          fY20: total.fY20 + (sp.subtotals?.fY20 || 0),
          fY19: total.fY19 + (sp.subtotals?.fY19 || 0),
        }), {
          fY25: 0, fY24: 0, fY23: 0, fY22: 0, fY21: 0, fY20: 0, fY19: 0
        });

        return (
          <div className="table-section salesperson-table-container">
            <div className="table-header"><Users /><h3>Salesperson View</h3></div>
            <div className="table-scroll-container">
              <table className="styled-table salesperson-table">
                <thead>
                  <tr>
                    <th>Salesperson</th>
                    <th>Customer</th>
                    <th>FY25</th>
                    <th>FY24</th>
                    <th>FY23</th>
                    <th>FY22</th>
                    <th>FY21</th>
                    <th>FY20</th>
                    <th>FY19</th>
                  </tr>
                </thead>
                <tbody>
                  {salespersonData.map((sp, idx) => (
                    <React.Fragment key={idx}>
                      {(sp.customers || []).map((cust, cidx) => (
                        <tr key={`${idx}-${cidx}`} className="customer-row">
                          {cidx === 0 && (
                            <td
                              rowSpan={(sp.customers || []).length + 1}
                              className="salesperson-cell"
                            >
                              <strong>{sp.salesperson}</strong>
                            </td>
                          )}
                          <td>{cust.customer}</td>
                          <td className="amount">₹{formatAmount(cust.fY25)}</td>
                          <td className="amount">₹{formatAmount(cust.fY24)}</td>
                          <td className="amount">₹{formatAmount(cust.fY23)}</td>
                          <td className="amount">₹{formatAmount(cust.fY22)}</td>
                          <td className="amount">₹{formatAmount(cust.fY21)}</td>
                          <td className="amount">₹{formatAmount(cust.fY20)}</td>
                          <td className="amount">₹{formatAmount(cust.fY19)}</td>
                        </tr>
                      ))}

                      <tr className="subtotal-row">
                        <td className="subtotal-label"><strong>Subtotal</strong></td>
                        <td className="amount subtotal-amount"><strong>₹{formatAmount(sp.subtotals.fY25)}</strong></td>
                        <td className="amount subtotal-amount"><strong>₹{formatAmount(sp.subtotals.fY24)}</strong></td>
                        <td className="amount subtotal-amount"><strong>₹{formatAmount(sp.subtotals.fY23)}</strong></td>
                        <td className="amount subtotal-amount"><strong>₹{formatAmount(sp.subtotals.fY22)}</strong></td>
                        <td className="amount subtotal-amount"><strong>₹{formatAmount(sp.subtotals.fY21)}</strong></td>
                        <td className="amount subtotal-amount"><strong>₹{formatAmount(sp.subtotals.fY20)}</strong></td>
                        <td className="amount subtotal-amount"><strong>₹{formatAmount(sp.subtotals.fY19)}</strong></td>
                      </tr>

                      {idx < salespersonData.length - 1 && (
                        <tr className="spacer-row">
                          <td colSpan="9" style={{ height: '10px', backgroundColor: '#f8fafc' }}></td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  
                  {/* Grand Total Row */}
                  <tr className="grandtotal-row">
                    <td colSpan="2" className="grandtotal-label">
                      <strong>Grand Total</strong>
                    </td>
                    <td className="amount grandtotal-amount">
                      <strong>₹{formatAmount(grandTotal.fY25)}</strong>
                    </td>
                    <td className="amount grandtotal-amount">
                      <strong>₹{formatAmount(grandTotal.fY24)}</strong>
                    </td>
                    <td className="amount grandtotal-amount">
                      <strong>₹{formatAmount(grandTotal.fY23)}</strong>
                    </td>
                    <td className="amount grandtotal-amount">
                      <strong>₹{formatAmount(grandTotal.fY22)}</strong>
                    </td>
                    <td className="amount grandtotal-amount">
                      <strong>₹{formatAmount(grandTotal.fY21)}</strong>
                    </td>
                    <td className="amount grandtotal-amount">
                      <strong>₹{formatAmount(grandTotal.fY20)}</strong>
                    </td>
                    <td className="amount grandtotal-amount">
                      <strong>₹{formatAmount(grandTotal.fY19)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      };

      return (
        <div className="report-container" ref={dashboardRef}>
          <div className="report-title">Sales Dashboard</div>
          {error && <div className="error-message">{error}</div>}

          {/* Filters */}
          <div className="filter-section">
            <div className="filter-card">
              <label>Organisation ID</label>
              <select name="orgId" value={formData.orgId} onChange={handleChange}>
                {orgIds.map((id, idx) => <option key={idx} value={id}>{id}</option>)}
              </select>
            </div>

            <div className="filter-card">
              <label>Company Code</label>
              <select name="companyCode" value={formData.companyCode} onChange={handleChange} disabled={!formData.orgId}>
                <option value="">All Company Codes</option>
                {companyCodes.map((code, idx) => <option key={idx} value={code}>{code}</option>)}
              </select>
            </div>

            <div className="filter-card">
              <label>Fiscal Year</label>
              <select name="fy" value={formData.fy} onChange={handleChange}>
                <option value="">All Fiscal Years</option>
                {financialYears.map((fy, idx) => <option key={idx} value={String(fy)}>{fy}</option>)}
              </select>
            </div>

            <div className="filter-card posting-date-with-button">
              <label>Posting Date</label>
              <div className="posting-date-container">
                <input type="date" name="postingDate" value={formData.postingDate} onChange={handleChange} />
                <button className="download-btn pdf-btn" onClick={handleDownloadPDF} disabled={pdfLoading}>
                  {pdfLoading ? <><div className="spinner"></div>Generating PDF...</> : <><FileText size={20} />Export to PDF</>}
                </button>
              </div>
            </div>
          </div>

          {/* Chart Controls */}
          <div className="chart-controls-section">
            <div className="chart-controls-card">
              <span className="chart-controls-label">Select Charts to Display:</span>
              <button onClick={() => toggleChartVisibility('revenue')} className={`chart-toggle-btn ${chartVisibility.revenue ? 'active' : ''}`}>
                {chartVisibility.revenue ? <Eye size={16} /> : <EyeOff size={16} />}
                <TrendingUp size={16} /> Revenue
              </button>
              <button onClick={() => toggleChartVisibility('customerSegments')} className={`chart-toggle-btn ${chartVisibility.customerSegments ? 'active' : ''}`}>
                {chartVisibility.customerSegments ? <Eye size={16} /> : <EyeOff size={16} />}
                <Users size={16} /> Customer Segments
              </button>
              <button onClick={() => toggleChartVisibility('weeklySales')} className={`chart-toggle-btn ${chartVisibility.weeklySales ? 'active' : ''}`}>
                {chartVisibility.weeklySales ? <Eye size={16} /> : <EyeOff size={16} />}
                <Calendar size={16} /> Weekly Sales
              </button>
            </div>
          </div>

          {pdfLoading && (
            <div className="pdf-loading">
              <div className="spinner"></div>
              <p>Generating PDF report...</p>
            </div>
          )}

          {/* Dashboard Content */}
          <div className="dashboard-content">
            {loading && chartLoading ? (
              <div className="dashboard-loading">
                <div className="spinner large"></div>
                <p>Loading your organization data...</p>
              </div>
            ) : (
              <>
                {anyChartVisible && (
                  <div className="charts-section" style={{ gridTemplateColumns: `repeat(${Object.values(chartVisibility).filter(v => v).length}, 1fr)` }}>
                    {renderRevenueChart()}
                    {renderCustomerSegments()}
                    {renderWeeklySalesChart()}
                  </div>
                )}
                <div className="tables-section">
                  {renderSalesTable()}
                  {renderSalespersonView()}
                </div>
              </>
            )}
          </div>
        </div>
      );
    };

    export default SalesDashboard;